/**
 * EMON NFT Bonding Program IDL
 * Program ID: FcR1r39QK7emuG3Yp2YV9Y3Rv7XjaB5M6B8RCeUBJSn5
 * 
 * IMPORTANT: USD is the REFERENCE VALUE (stable)
 * EMON is the payment token (price can fluctuate)
 * 
 * Example: If NFT costs $1 USD and EMON rate is 0.01 USD/EMON
 * Then user pays 100 EMON (which equals $1 USD at current rate)
 */

export const EMON_NFT_PROGRAM_ID = "FcR1r39QK7emuG3Yp2YV9Y3Rv7XjaB5M6B8RCeUBJSn5";

// EMON token has 6 decimals
export const EMON_DECIMALS = 6;

// Default EMON/USD rate (1 EMON = X USD) - should be fetched dynamically
// This is a fallback; real rate should come from oracle/API/database
// At this rate: $1 USD / 0.002 = ~500 EMON
let CURRENT_EMON_USD_RATE = 0.002;

// Base price in USD (stable reference)
export const BASE_PRICE_USD = 1.0; // $1 USD

// Fee in USD (stable reference)
export const FEE_USD = 0.01; // $0.01 USD

// Update the EMON/USD rate (call this when fetching new rate)
export function setEmonUsdRate(rate: number): void {
  CURRENT_EMON_USD_RATE = rate;
  console.log(`EMON/USD rate updated to: ${rate}`);
}

// Get current EMON/USD rate
export function getEmonUsdRate(): number {
  return CURRENT_EMON_USD_RATE;
}

// Convert USD to EMON (USD is reference, EMON is calculated)
// Returns the amount in smallest units (with 6 decimals applied)
export function usdToEmon(usdAmount: number): number {
  if (CURRENT_EMON_USD_RATE <= 0) return 0;
  // Calculate how many whole EMON tokens needed
  const emonTokens = usdAmount / CURRENT_EMON_USD_RATE;
  // Convert to smallest unit (6 decimals) for SPL Token transfer
  return Math.floor(emonTokens * Math.pow(10, EMON_DECIMALS));
}

// Convert EMON smallest units to USD (for display purposes)
export function emonToUsd(emonSmallestUnits: number): number {
  const emonTokens = emonSmallestUnits / Math.pow(10, EMON_DECIMALS);
  return emonTokens * CURRENT_EMON_USD_RATE;
}

// Get EMON tokens (human readable) from smallest units
export function emonToTokens(emonSmallestUnits: number): number {
  return emonSmallestUnits / Math.pow(10, EMON_DECIMALS);
}

// BONDING CURVE CALCULATIONS REMOVED FROM FRONTEND
// All price calculations must come from the backend (record-nft-sale edge function)
// Frontend only displays prices from database
// DO NOT ADD calculateNextPriceUsd or similar functions here

// IDL for the Anchor program
export const EMON_NFT_BONDING_IDL = {
  version: "0.1.0",
  name: "emon_nft_bonding",
  instructions: [
    {
      name: "initializeNft",
      accounts: [
        { name: "nftState", isMut: true, isSigner: false },
        { name: "creator", isMut: true, isSigner: true },
        { name: "systemProgram", isMut: false, isSigner: false },
      ],
      args: [],
    },
    {
      name: "buyNft",
      accounts: [
        { name: "nftState", isMut: true, isSigner: false },
        { name: "buyer", isMut: true, isSigner: true },
        { name: "seller", isMut: true, isSigner: false },
      ],
      args: [],
    },
    {
      name: "freezeNft",
      accounts: [
        { name: "nftState", isMut: true, isSigner: false },
      ],
      args: [],
    },
    {
      name: "shadowBanNft",
      accounts: [
        { name: "nftState", isMut: true, isSigner: false },
      ],
      args: [],
    },
  ],
  accounts: [
    {
      name: "NftState",
      type: {
        kind: "struct",
        fields: [
          { name: "owner", type: "publicKey" },
          { name: "priceEmon", type: "u64" },
          { name: "salesCount", type: "u32" },
          { name: "isFrozen", type: "bool" },
          { name: "isShadowBanned", type: "bool" },
        ],
      },
    },
  ],
  events: [
    {
      name: "NftSold",
      fields: [
        { name: "nft", type: "publicKey", index: false },
        { name: "oldPrice", type: "u64", index: false },
        { name: "newPrice", type: "u64", index: false },
        { name: "buyer", type: "publicKey", index: false },
      ],
    },
  ],
  errors: [
    { code: 6000, name: "NftFrozen", msg: "NFT is frozen" },
    { code: 6001, name: "WalletBanned", msg: "Wallet is banned" },
    { code: 6002, name: "InsufficientFunds", msg: "Buyer has insufficient funds" },
    { code: 6003, name: "MathOverflow", msg: "Math overflow" },
  ],
};

// TypeScript types for program state
export interface NftState {
  owner: string;
  priceEmon: bigint;
  salesCount: number;
  isFrozen: boolean;
  isShadowBanned: boolean;
}

export interface NftSoldEvent {
  nft: string;
  oldPrice: bigint;
  newPrice: bigint;
  buyer: string;
}

// Program error codes
export enum EmonNftError {
  NftFrozen = 6000,
  WalletBanned = 6001,
  InsufficientFunds = 6002,
  MathOverflow = 6003,
}

export function getErrorMessage(code: number): string {
  switch (code) {
    case EmonNftError.NftFrozen:
      return "This NFT is frozen and cannot be traded";
    case EmonNftError.WalletBanned:
      return "Your wallet is banned from trading";
    case EmonNftError.InsufficientFunds:
      return "Insufficient funds to complete purchase";
    case EmonNftError.MathOverflow:
      return "Price calculation error";
    default:
      return "Unknown error occurred";
  }
}
